/**
 * Which record a CREATE run made for the table that launched it, and whether
 * Save & Go is safe.
 *
 * A run reports every record it inserts as a `BOS_CREATE_COMPLETED` socket
 * frame carrying the run's correlation id. A create on the Samples table may
 * first write the host records the sample hangs off (sourcing, supplier link),
 * so the frames are read against the table the action sits on, not a map.
 */

import type { CreateCompletedFrame } from "../notifications/readCreateCompleted";

export type CreateSubmitIntent = "stay" | "new" | "go";

/** True when the frame is a row of the launching table. */
export const isTableRow = (frame: CreateCompletedFrame, tableResource: string): boolean =>
  frame.resource === tableResource;

/**
 * The created record the launching table should open.
 *
 * A frame for the table's own resource wins. Otherwise the run wrote its rows
 * somewhere else (a sample made from the Materials list), and the last create
 * is the one the earlier host records were written for: a child cannot be
 * created before its parent exists.
 */
export const chooseCreatedRecord = (
  frames: readonly CreateCompletedFrame[],
  tableResource: string,
): CreateCompletedFrame | undefined =>
  frames.find((frame) => isTableRow(frame, tableResource)) ?? frames.at(-1);

/** How many samples the current form would create. A form without a matrix creates one. */
export const expectedSampleCount = (values: Record<string, unknown>): number => {
  const samples = values.samples;

  if (!Array.isArray(samples) || samples.length === 0) {
    return 1;
  }

  let total = 0;

  for (const cell of samples) {
    if (!cell || typeof cell !== "object") {
      total += 1;
      continue;
    }

    const quantity = (cell as { quantity?: unknown }).quantity;
    const count = typeof quantity === "number" ? quantity : Number(quantity);

    total += Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  return total;
};

/** Save & Go opens one record. A batch matrix has no single destination. */
export const allowsSaveAndGo = (values: Record<string, unknown>): boolean =>
  expectedSampleCount(values) <= 1;
